import { mutations as gqMutations, queries } from '@/graphql'
import { apolloClient } from '@/graphql/client'
import {
  ChannelPartnerOrganisation,
  DiscountType,
  Draft,
  DraftPartyVariant,
  FeeSummary,
  Identification,
  IdentificationEntityType,
  IdentificationStep,
  ProductDiscount,
  ProductSummary,
  UpdateDraftDeliveryDetailsInput,
  UpdateDraftInput,
  UpdateIdentificationInput
} from '@/graphql/types'
import { RootState } from '@/store'
import { featureEnabledFor } from '@/utils'
import { ApolloQueryResult } from 'apollo-client'
import { ToastProgrammatic } from 'buefy'
import { pick } from 'lodash'
import get from 'lodash/get'
import { v4 as uuid } from 'uuid'
import Vue from 'vue'
import { parse } from 'vue-currency-input'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { CURRENCY_FORMAT_OPTIONS_MINZERO, PERCENTAGE_FORMAT_OPTIONS } from '~/constants'

interface IdentificationOverWriterProperties {
  isRegisteredAddressSameAsPrincipalPlaceOfBusiness: boolean
  selectedPostalAddress: null | string
}

interface IdentificationState {
  draftId: string | null
  organisationId: string | null
  editedIdentification: Partial<UpdateIdentificationInput>
  editedDraft: Partial<Draft>
  editedChannelPartners: ChannelPartnerOrganisation[]
  identificationOverWriters: IdentificationOverWriterProperties
  feeSummaries: FeeSummary[]
}

// this should be refactored to use separate object (UpdateDraftInput) rather than mutatuing draft object
const UPDATE_DRAFT_ALLOWED_FIELDS: Array<keyof UpdateDraftInput> = [
  'acceptedChannelPartnerId',
  'additionalSupplierDetail',
  'amount',
  'applyAvailableReferralDiscount',
  'attachmentCreates',
  'attachmentDeletes',
  'comment',
  'contractUpdates',
  'deliveryDetails',
  'description',
  'directorEmailAddress',
  'directorFirstName',
  'directorLastName',
  'discountPercentage',
  'entityWealthAccumulation',
  'establishmentFeeDiscountAmount',
  'expectedReturnDate',
  'expiresAt',
  'facilityId',
  'feeSummaryId',
  'fundingMethod',
  'industry',
  'industryAdditionalDetails',
  'invoiceRecipientEmailAddresses',
  'issuerDetailsOfSourceOfFunds',
  'issuerSourceOfFunds',
  'partyCreates',
  'partyDeletes',
  'partyUpdates',
  'productTermsAccepted',
  'purpose',
  'referenceNumber',
  'referralPercentage',
  'refundAccountName',
  'refundAccountNumber',
  'refundBSB',
  'requestedSuretyDocumentCreates',
  'requestedSuretyDocumentDeletes',
  'requestedSuretyDocumentUpdates',
  'specifiedOtherPurpose',
  'stripeSubscriptionId',
  'stripeSubscriptionStatus'
]

// Getters
export const GET_FEE_SUMMARIES = 'getFeeSummaries'

const createInitialState = (): IdentificationState => ({
  draftId: null,
  organisationId: null,
  editedIdentification: {},
  editedDraft: {},
  editedChannelPartners: [],
  identificationOverWriters: {
    isRegisteredAddressSameAsPrincipalPlaceOfBusiness: false,
    selectedPostalAddress: null
  },
  feeSummaries: []
})

const state = createInitialState()

const getters: GetterTree<IdentificationState, RootState> = {
  getOrganisationId: state => state.organisationId,
  getIdentificationOverWriters: state => state.identificationOverWriters,
  getIdentification: state => state.editedIdentification,
  getDraft: state => state.editedDraft,
  getChannelPartners: state => state.editedChannelPartners,
  [GET_FEE_SUMMARIES]: state => state.feeSummaries
}

const actions: ActionTree<IdentificationState, RootState> = {
  fetchData({ commit }, data) {
    const draftId = data?.draftId || state.draftId
    const organisationId = data?.organisationId || state.organisationId
    if (!draftId || !organisationId) return
    return apolloClient()
      .query({
        query: queries.DRAFT_BG_SUBMISSION_QUERY,
        variables: { draftId, organisationId }
      })
      .then(({ data }) => {
        const { identification, ...draft } = get(data, 'draft', {})
        const organisation = identification.organisation
        delete identification.organisation
        if (!draft.deliveryDetails)
          draft.deliveryDetails = {
            address: '',
            recipientFamilyName: '',
            recipientGivenName: '',
            phoneNumber: '',
            companyName: ''
          }
        if (featureEnabledFor(draft.feeSummary?.supplier?.name, 'additionalSupplierDetail'))
          draft.additionalSupplierDetail = draft.additionalSupplierDetail ?? {
            hasFundingAccountAuthorisation: false,
            fundingAccountBsb: '',
            fundingAccountNumber: ''
          }
        else {
          draft.additionalSupplierDetail = null
        }
        draft.invoiceRecipientEmailAddresses = draft.invoiceRecipientEmailAddresses?.length
          ? draft.invoiceRecipientEmailAddresses
          : ''
        if (!draft.identificationSubmitted && identification.step === IdentificationStep.SUBMITTED) {
          identification.step = IdentificationStep.REVIEW
        }
        commit('setIdentification', identification)
        commit('setDraft', draft)
        commit('setOrganisationId', organisationId)
        commit('setDraftId', draftId)
        commit('setChannelPartners', organisation?.channelPartners ?? [])
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },

  fetchFeeSummaries({ commit }, data) {
    const organisationId = data?.organisationId || state.organisationId
    if (!organisationId) return

    return apolloClient()
      .query({
        query: queries.PRODUCT_SELECTION_SUMMARY,
        variables: { organisationId }
      })
      .then(({ data }: ApolloQueryResult<{ productSelectionSummary: ProductSummary[] }>) => {
        commit('setFeeSummaries', data.productSelectionSummary)
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },

  async completeIdentification({ state }) {
    const { editedIdentification } = state
    // this might affect greenID flow which has the step of SUBMITTED for completed identifications
    if (!editedIdentification.id || editedIdentification.step !== IdentificationStep.SUBMITTED) return

    return await apolloClient()
      .mutate({
        mutation: gqMutations.IDENTIFICATION_COMPLETE_MUTATION,
        variables: { identificationId: editedIdentification.id, clientMutationId: uuid() }
      })
      .then(data => {
        return data
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },

  async updateIdentification({ state }, clientCompleted: boolean) {
    const { editedIdentification, identificationOverWriters } = state
    if (!editedIdentification.id) return

    const isCompleted = clientCompleted === true && editedIdentification.step === IdentificationStep.SUBMITTED
    if (isCompleted) {
      return this.dispatch('bgSubmission/completeIdentification')
    }

    const input: UpdateIdentificationInput = {
      clientMutationId: uuid(),
      id: editedIdentification.id,
      //never flag as completed unless specifically requested to do so
      companyDetailsACN: editedIdentification.companyDetailsACN,
      companyDetailsIsListed: editedIdentification.companyDetailsIsListed,
      companyDetailsIsRegulated: editedIdentification.companyDetailsIsRegulated,
      companyDetailsIsSubsidiaryListed: editedIdentification.companyDetailsIsSubsidiaryListed,
      companyDetailsIsNormalType: editedIdentification.companyDetailsIsNormalType,
      companyDetailsHasBeneficialOwners: editedIdentification.companyDetailsHasBeneficialOwners,
      companyDetailsListedExchangeName: editedIdentification.companyDetailsListedExchangeName,
      companyDetailsName: editedIdentification.companyDetailsName,
      identificationIndustry: editedIdentification.identificationIndustry,
      industryAdditionalDetails: editedIdentification.industryAdditionalDetails,
      entityWealthAccumulation: editedIdentification.entityWealthAccumulation,
      companyDetailsNatureOfBusiness: editedIdentification.companyDetailsNatureOfBusiness,
      companyDetailsPrincipalPlaceOfBusiness: editedIdentification.companyDetailsPrincipalPlaceOfBusiness,
      companyDetailsRegisteredAddress: editedIdentification.companyDetailsRegisteredAddress,
      companyDetailsRegulatorLicenseNumber: editedIdentification.companyDetailsRegulatorLicenseNumber,
      companyDetailsRegulatorName: editedIdentification.companyDetailsRegulatorName,
      companyDetailsSubsidiaryListedCompanyName: editedIdentification.companyDetailsSubsidiaryListedCompanyName,
      companyDetailsSubsidiaryListedExchangeName: editedIdentification.companyDetailsSubsidiaryListedExchangeName,
      companyDetailsTaxFatcaStatus: editedIdentification.companyDetailsTaxFatcaStatus,
      companyDetailsTaxFatcaStatusOther: editedIdentification.companyDetailsTaxFatcaStatusOther,
      companyDetailsTaxForeignResidentCountry: editedIdentification.companyDetailsTaxForeignResidentCountry,
      companyDetailsTaxGIIN: editedIdentification.companyDetailsTaxGIIN,
      companyDetailsTaxHasBeneficialOwnerForeignResident:
        editedIdentification.companyDetailsTaxHasBeneficialOwnerForeignResident,
      companyDetailsTaxHasGIIN: editedIdentification.companyDetailsTaxHasGIIN,
      companyDetailsTaxIsForeignResident: editedIdentification.companyDetailsTaxIsForeignResident,
      companyDetailsTaxType: editedIdentification.companyDetailsTaxType,
      companyDetailsOnlyOperatesInAustralia: editedIdentification.companyDetailsOnlyOperatesInAustralia,
      dataType: editedIdentification.dataType,
      entityType: editedIdentification.entityType,
      hasMacAccount: editedIdentification.hasMacAccount,
      hasDividendsIncome: editedIdentification.hasDividendsIncome,
      postalAddress: editedIdentification.postalAddress,
      taxExemptionDetails: editedIdentification.taxExemptionDetails,
      taxExemptionType: editedIdentification.taxExemptionType,
      taxStatus: editedIdentification.taxStatus,
      step: editedIdentification.step,
      individuals: editedIdentification.individuals
    }

    // OverWriters
    if (identificationOverWriters.selectedPostalAddress && identificationOverWriters.selectedPostalAddress !== 'OTHER')
      input.postalAddress = identificationOverWriters.selectedPostalAddress
    if (
      input.entityType === IdentificationEntityType.COMPANY &&
      identificationOverWriters.isRegisteredAddressSameAsPrincipalPlaceOfBusiness
    )
      input.companyDetailsPrincipalPlaceOfBusiness = input.companyDetailsRegisteredAddress
    return await apolloClient()
      .mutate({
        mutation: gqMutations.IDENTIFICATION_UPDATE_MUTATION,
        variables: { input }
      })
      .then(data => {
        return data
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },
  async commitDraft({ state }) {
    const { editedDraft, draftId, organisationId } = state
    if (!draftId || !organisationId) return
    // this should be refactored to create a new UpdateDraftInput object and explicitly set properties rather than deleting properties from Draft object
    const draftInput = pick(editedDraft, UPDATE_DRAFT_ALLOWED_FIELDS) as Partial<UpdateDraftInput>

    // commitDraft is used by multiple compoenents, so preserve any existing delivery details that aren't being edited in this action
    // this can be further refactored which requires a more significant change to the structure of the store
    draftInput.deliveryDetails = {
      ...editedDraft.deliveryDetails,
      deliveryType: editedDraft.deliveryType
    } as UpdateDraftDeliveryDetailsInput
    const beneficiary = editedDraft.parties?.find(party => party.partyVariant === DraftPartyVariant.BENEFICIARY)
    if (beneficiary?.primaryContact?.email) {
      draftInput.partyUpdates = [
        {
          id: beneficiary.id,
          primaryContact: beneficiary.primaryContact
        }
      ]
    }

    const input: UpdateDraftInput = {
      clientMutationId: uuid(),
      id: draftId,
      organisationId: organisationId,
      ...draftInput
    }

    return await apolloClient()
      .mutate({
        mutation: gqMutations.DRAFT_BG_SUBMISSION_MUTATION,
        variables: { input }
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },
  commitFeeSelection({ state }, draftInput: UpdateDraftInput) {
    const { editedDraft, draftId, organisationId } = state
    if (!draftId && !organisationId) return
    const { feeSummaryId, acceptedChannelPartnerId, establishmentFeeDiscountAmount, discountPercentage } = editedDraft
    const input: UpdateDraftInput = {
      clientMutationId: uuid(),
      id: draftId!,
      organisationId: organisationId!,
      feeSummaryId,
      productTermsAccepted: draftInput.productTermsAccepted,
      acceptedChannelPartnerId: acceptedChannelPartnerId ?? null!,
      establishmentFeeDiscountAmount: establishmentFeeDiscountAmount ? establishmentFeeDiscountAmount : 0,
      discountPercentage: discountPercentage ? discountPercentage : 0,
      additionalSupplierDetail: editedDraft.additionalSupplierDetail,
      facilityId: editedDraft.facilityId ?? undefined
    }
    return apolloClient()
      .mutate({
        mutation: gqMutations.DRAFT_BG_SUBMISSION_MUTATION,
        variables: { input }
      })
      .catch(error => ToastProgrammatic.open({ message: error.message, type: 'is-danger' }))
  },
  clearBgSubmission({ commit }) {
    commit('clearBgSubmission')
  }
}

const mutations: MutationTree<IdentificationState> = {
  setDraftId(state, draftId) {
    state.draftId = draftId
  },
  setOrganisationId(state, organisationId) {
    state.organisationId = organisationId
  },
  setChannelPartners(state, channelPartners) {
    state.editedChannelPartners = channelPartners
  },
  setIdentification(state, identification) {
    state.editedIdentification = identification
  },
  updateIdentification(state, changes: [Partial<Identification>]) {
    Object.entries(changes).forEach(([key, value]) => ((state.editedIdentification as any)[key] = value))
  },
  updateIdentificationOverWriters(state, changes: [Partial<IdentificationOverWriterProperties>]) {
    Object.entries(changes).forEach(([key, value]) => ((state.identificationOverWriters as any)[key] = value))
  },
  setDraft(state, draft) {
    state.editedDraft = draft
  },
  setDraftFeeWithProductDiscount(
    state,
    {
      feeSummary,
      productDiscount,
      facilityId
    }: { feeSummary: FeeSummary; productDiscount?: ProductDiscount; facilityId: string }
  ) {
    state.editedDraft.feeSummaryId = feeSummary.id
    state.editedDraft.acceptedChannelPartnerId = productDiscount?.channelPartnerId
    state.editedDraft.facilityId = facilityId

    state.editedDraft.discountPercentage =
      productDiscount?.discountType === DiscountType.ONGOING_PERCENTAGE
        ? parse(productDiscount?.discountLabel, PERCENTAGE_FORMAT_OPTIONS)
        : 0

    state.editedDraft.establishmentFeeDiscountAmount =
      productDiscount?.discountType === DiscountType.ESTABLISHMENT_FEE_AMOUNT
        ? parse(productDiscount?.discountLabel, CURRENCY_FORMAT_OPTIONS_MINZERO)
        : 0
  },
  updateDraft(state, changes: [Partial<Draft>]) {
    Object.entries(changes).forEach(([key, value]: any) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([childKey, childValue]) => {
          Vue.set(state.editedDraft[key], childKey, childValue)
        })
      } else {
        Vue.set(state.editedDraft, key, value)
      }
    })
  },
  setFeeSummaries(state, getFeeSummaries) {
    state.feeSummaries = getFeeSummaries
  },
  clearBgSubmission(state) {
    Object.assign(state, createInitialState())
  }
}

const mod: Module<IdentificationState, RootState> = {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}

export default mod
